import { React, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import {
  get_kid_channel,
  get_more_kid_channel,
  add_channel_subscriber,
} from "../../../redux/features/ktkSlice";
import ktkService from "../../../services/ktkService";

import { Button, LinkButton } from "../../commonComponents/Buttons";
import { TripleGrid } from "../../commonComponents/Grids";
import { ConfirmationModal } from "../../modals";

import {
  ViewsIcon,
  UnlikedIcon,
  LikedIcon,
  NoAvatar,
  CoinIcon,
  DefaultCover,
  ReplyButton,
  ReplyButton3,
} from "../../../assets";
import "./kidsTeachKids.scss";

function KidChannel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const [kidChannel, setKidChannel] = useState();
  const [kidChannelVideos, setKidChannelVideos] = useState();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [visibleRemoveVideoModal, setVisibleRemoveVideoModal] = useState(false);
  const [neededInfo, setNeededInfo] = useState();
  const userId = useSelector(state => state.user.userInfo.id);

  useEffect(() => {
    if (!kidChannel) {
      dispatch(get_kid_channel(params.channelId)).then(result => {
        setKidChannel(result?.payload);
        const videos = result.payload.data.videos || [];
      
        const enriched = videos.map(video => {
          const commentCount = video.comment_count;
          const key = `video-${video.id}-lastSeenCount-user${userId}`;
          const prev = parseInt(localStorage.getItem(key) || "0", 10);
          const hasNewCount = commentCount > prev;
          const isOthersComment = video.last_comment_user_id !== userId;
          const hasNew = hasNewCount && isOthersComment;
          return { ...video, hasNewComments: hasNew };
        });
      
        setKidChannelVideos(enriched);
        setIsSubscribed(result.payload.data.is_subscribed);
      });      
    }
  }, [kidChannel, dispatch, params, userId]);
  
  function handleSubscribe() {
    dispatch(add_channel_subscriber(params.channelId))
      .unwrap()
      .then(({ is_subscribed }) => {
        setIsSubscribed(is_subscribed);
      })
      .catch(console.error);
  }  
  function likeHandler(videoId) {
    ktkService.addVideoLike(params?.channelId, videoId).then((result) => {
      let tempArray = [...kidChannelVideos];
      const index = kidChannelVideos.findIndex((item) => item?.id === videoId);
      tempArray[index] = {
        ...tempArray[index],
        additional_info: {
          liked: result?.data?.result?.liked,
          watched: tempArray[index].additional_info.watched,
        },
        likes: result?.data?.result?.counter,
      };

      setKidChannelVideos(tempArray);
    });
  }

  function deleteVideo(videoId) {
    ktkService.deleteVideo(params?.channelId, videoId).then(() => {
      dispatch(get_kid_channel(params?.channelId)).then((result) => {
        setKidChannel(result?.payload);
        setKidChannelVideos(result?.payload?.data?.videos);
        setVisibleRemoveVideoModal(false);
      });
    });
  }

  function loadMore() {
    let args = {
      channelId: params?.channelId,
      page: kidChannel?.next_page,
    };
    dispatch(get_more_kid_channel(args)).then((result) => {
      setKidChannel(result?.payload);
      setKidChannelVideos(
        kidChannelVideos.concat(result?.payload?.data?.videos)
      );
    });
  }

  function renderTitle() {
    if (kidChannel && kidChannel?.data?.user?.request_user) {
      return t("myVids");
    } else if (kidChannel && !kidChannel?.data?.user?.request_user) {
      return `${kidChannel?.data?.user?.name}${t("videoOwner")}`;
    }
  }

  function renderOwnerInfo() {
    if (kidChannel && kidChannel?.data?.user?.age) {
      return `${kidChannel?.data?.user?.name}, ${
        kidChannel?.data?.user?.age
      } ${t("years")}`;
    } else if (kidChannel && !kidChannel?.data?.user?.age) {
      return `${kidChannel?.data?.user?.name}, ? ${t("years")}`;
    }
  }

  function disableLinkClick(e, item) {
    if (item?.status === "pending" || item?.status === "uploading") {
      e.preventDefault();
    }
  }

  function markVideoSeen(videoId, commentCount) {
    const key = `video-${videoId}-lastSeenCount-user${userId}`;
    localStorage.setItem(key, String(commentCount));

    // also immediately update our in-memory flag so the badge disappears
    setKidChannelVideos(vs =>
      vs.map(v =>
        v.id === videoId ? { ...v, hasNewComments: false } : v
      )
    );
  }

  return (
    <main className="ktkBg">
      <Button
        label={t("back")}
        iconClassName="hasIcon--chevron-left"
        viewType="backButton"
        onClick={() => navigate('/kids-teach-kids/channels')}
      />
      <section className="withAsideArticle">
        <aside>
          <section className="channelInfo">
            <section className="mainInfo">
              <img
                className="userImage"
                src={
                  kidChannel?.data?.channel_avatar
                    ? kidChannel?.data?.channel_avatar
                    : NoAvatar
                }
                alt="User Name"
              />

              <div className="userInfo">
                <div className="textInfo">
                  {kidChannel?.data?.user?.request_user && (
                    <p className="ownerLabel">{t("thisIsYou")}</p>
                  )}
                  <p className="ownerInfo">{renderOwnerInfo()}</p>
                  <p className="channelTitle">{kidChannel?.data?.title}</p>
                </div>
                {kidChannel?.data?.user?.request_user && (
                  <LinkButton
                    customClassName="navButton withoutText defaultColor"
                    iconClassName="hasIcon--edit-2"
                    goTo="/kids-teach-kids/channel-management"
                    state={{
                      ifEdit: true,
                      kidChannel,
                    }}
                  />
                )}
              </div>
            </section>
                {!kidChannel?.data?.user?.request_user && (
                  <button
                    className="subscribeButton"
                    onClick={handleSubscribe}
                  >
                    {isSubscribed
                      ? "Unsubscribe"
                      : "Subscribe"}
                  </button>
                )}
            <p className="channelDescription">
              {kidChannel?.data?.description}
            </p>
          </section>
        </aside>

        <TripleGrid
          title={renderTitle()}
          viewType="middle"
          ifPagination={kidChannel?.next_page ? true : false}
          paginationClick={loadMore}
        >
          {kidChannel?.data?.user?.request_user && (
            <li>
              <NavLink
                to="/kids-teach-kids/video-upload"
                state={{
                  channelId: kidChannel?.data?.id,
                }}
                className="addVideoBtn"
              >
                +{t("uploadNewVideo")}
              </NavLink>
            </li>
          )}
          {kidChannelVideos &&
            kidChannelVideos.map((item) => {
              return (
                <li key={item?.id}>
                  <div className="videoCard">
                    <NavLink
                      className="videoLink"
                      to={`/kids-teach-kids/channel/${kidChannel?.data?.id}/video/${item?.id}`}
                      oonClick={(e) => disableLinkClick(e, item)}
                      onClick={e => {
                        disableLinkClick(e, item);
                        markVideoSeen(item.id, item.comment_count);
                      }}
                    >
                      <div className="innerElement">
                        <img
                          className={`videoCardImage  ${
                            (item?.status === "pending" ||
                              item?.status === "uploading") &&
                            "pendingImage"
                          }`}
                          src={
                            !item?.cover_image_link
                              ? DefaultCover
                              : item?.cover_image_link
                          }
                          alt="VideoTitle"
                        />
                        {item?.additional_info?.watched &&
                          !kidChannel?.data?.user?.request_user && (
                            <div className="videoWatched hasIcon--check"></div>
                          )}
                        {(item?.status === "pending" ||
                          item?.status === "uploading") &&
                          kidChannel?.data?.user?.request_user && (
                            <div className="cardLabel pendingLabel">
                              {t("pending")}
                            </div>
                          )}
                        {item?.status === "approved" &&
                          item?.additional_info?.approved_recently &&
                          kidChannel?.data?.user?.request_user && (
                            <div className="cardLabel approvedLabel">
                              {t("approved")}
                            </div>
                          )}
                      </div>
                    </NavLink>
                    <p className="videoTitle">{item?.title}</p>
                    {(item?.status !== "pending" ||
                      item?.status !== "uploading") && (
                      <div className="infoBlock">
                        <div className="leftBlock">
                          <div className="activityCounter">
                            <img
                              className="activityIcon"
                              src={ViewsIcon}
                              alt={t("views")}
                            />
                            <p className="activityTxt">{item?.views}</p>
                          </div>
                          <div className="activityCounter">
                            <Button
                              onClick={() => likeHandler(item?.id)}
                              viewType="clildrenBtn"
                            >
                              <img
                                className="activityIcon"
                                src={
                                  item?.additional_info?.liked
                                    ? LikedIcon
                                    : UnlikedIcon
                                }
                                alt={t("likes")}
                              />
                            </Button>
                            <p className="activityTxt">{item?.likes}</p>
                          </div>
                          <div className="activityCounter">
                            <img
                              className="activityIcon"
                              src={item.hasNewComments ? ReplyButton : ReplyButton3}
                              alt={t("views")}
                            />
                            <p className="activityTxt">{item?.comment_count}</p>
                          </div>
                        </div>
                        {kidChannel?.data?.user?.request_user ? (
                          <>
                            <Button
                              customClassName="trashBtn"
                              viewType="transparentIcon"
                              iconClassName="hasIcon--trash-2"
                              onClick={() => {
                                setVisibleRemoveVideoModal(true);
                                setNeededInfo(item?.id);
                              }}
                            />
                          </>
                        ) : (
                          <div className="pointCounter">
                            <img
                              className="videoIcon"
                              src={CoinIcon}
                              alt={t("points")}
                            />
                            <span className="earnedPointsCount">
                              {item?.additional_info?.watched ? 1 : 0}
                            </span>
                            <span className="maxPointsCount">/1</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          {visibleRemoveVideoModal && (
            <ConfirmationModal
              setConfirmationModal={setVisibleRemoveVideoModal}
              deleteFunction={deleteVideo}
              neededInfo={neededInfo}
              modalTitle={t("videoDelTitle")}
              modalLegend={t("videoDelLegend")}
              modalConfirmBtn={t("videoDelBtn")}
            />
          )}
        </TripleGrid>
      </section>
    </main>
  );
}

export default KidChannel;
